import { getToken, clearToken } from "./auth";
import { isRateLimitInfo, showRateLimitModal } from "../components/rate_limit_modal";

const BASE = "/api";

/** The rate-limit envelope out of a 429 body, or null if it is not one.
 *
 * A 429 that is not ours — a proxy's own throttling, say — falls through to the
 * generic error path rather than being described as a provider rate limit. */
async function readRateLimit(res: Response) {
  try {
    // Cloned, so a 429 that turns out not to be ours still has a readable body
    // when it reaches the generic error path below.
    const data = await res.clone().json();
    return isRateLimitInfo(data?.detail) ? data.detail : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/** Pull a readable sentence out of a FastAPI error body.
 *
 * `detail` is a string for the errors this app raises itself and an array of
 * `{loc, msg}` objects for a request the schema rejected. Only the first shape
 * was ever read, so a 422 — the URL cap being the first one a user can trigger
 * — reached the page as the bare status text "Unprocessable Entity". */
function errorDetail(data: unknown, fallback: string): string {
  const detail = (data as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => (typeof d?.msg === "string" ? d.msg : ""))
      // Pydantic prefixes a validator's own message with "Value error, ";
      // the sentence after it is the one written for a human to read.
      .map((m) => m.replace(/^Value error, /, ""))
      .filter(Boolean);
    if (messages.length) return messages.join(" ");
  }
  return fallback;
}

export async function api<T>(
  path: string,
  opts: {
    method?: string;
    body?: unknown;
    auth?: boolean;
    /** Abort the request after this many milliseconds.
     *
     * Off by default, which is what nearly every call here wants — the browser's
     * own timeout is fine for a request that answers in milliseconds. It is the
     * long ones that need it: `fetch` waits indefinitely, so a request the
     * server has quietly dropped leaves a disabled button and a spinner forever,
     * with no error to render. Set it above whatever ceiling the route observes
     * on its side, so a slow-but-alive request is never cut off. */
    timeoutMs?: number;
  } = {}
): Promise<T> {
  const { method = "GET", body, auth = true, timeoutMs } = opts;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller
    ? window.setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller?.signal,
    });
  } catch (err) {
    // An abort is the one fetch rejection worth naming: the request may well
    // have been completing on the server, so "failed" would be a guess.
    if (controller?.signal.aborted) {
      throw new ApiError(
        408,
        `The request took longer than ${Math.round(timeoutMs! / 1000)}s and was given up on. ` +
          "It may still have finished on the server — check before retrying."
      );
    }
    throw err;
  } finally {
    if (timer !== null) window.clearTimeout(timer);
  }

  if (res.status === 401 && auth) {
    clearToken();
    if (location.pathname !== "/login") location.href = "/login";
    throw new ApiError(401, "Unauthorized");
  }

  // The second global side effect in this function, and deliberately shaped
  // like the first: 401 redirects to login from wherever you were, because
  // every caller would otherwise have to handle a dead session; 429 raises the
  // rate-limit modal, because every caller would otherwise have to explain a
  // provider limit in its own words — and the two that tried produced raw
  // Google JSON in a table cell and an error document saved as the daily brief.
  //
  // It still throws afterwards. The modal explains; the call site is left to
  // decide what its page should do about a request that did not happen.
  if (res.status === 429) {
    const info = await readRateLimit(res);
    if (info) {
      showRateLimitModal(info);
      throw new ApiError(429, info.message ?? "Rate limited — try again shortly.");
    }
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      detail = errorDetail(await res.json(), res.statusText);
    } catch {}
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
